import { useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pageImages } from '../../data/siteData';
import PagePhotoViewer from '../../elements/PagePhotoViewer';

const images = pageImages['cart-lifters-dumpers-dump-carts'];


function CartLiftersDumpersDumpCarts() {
  const cartCarouselRef = useRef(null);
  const dragState = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const [activeCartSlide, setActiveCartSlide] = useState(0);

  const updateCartSlide = () => {
    const track = cartCarouselRef.current;
    if (!track) return;

    const cards = Array.from(track.querySelectorAll('.cart-option-card'));
    const maxScroll = track.scrollWidth - track.clientWidth;
    const slideTargets = cards.map((card) => Math.min(card.offsetLeft - track.offsetLeft, maxScroll));

    if (track.scrollLeft >= maxScroll - 4) {
      setActiveCartSlide(cards.length - 1);
      return;
    }

    let currentIndex = 0;
    for (let index = 1; index < slideTargets.length; index += 1) {
      const threshold = (slideTargets[index - 1] + slideTargets[index]) / 2;
      if (track.scrollLeft >= threshold) currentIndex = index;
    }

    setActiveCartSlide(currentIndex);
  };

  const moveCartCarousel = (direction) => {
    const track = cartCarouselRef.current;
    if (!track) return;

    const cards = Array.from(track.querySelectorAll('.cart-option-card'));
    const nextIndex = Math.min(Math.max(activeCartSlide + direction, 0), cards.length - 1);
    const nextCard = cards[nextIndex];
    const maxScroll = track.scrollWidth - track.clientWidth;

    track.scrollTo({
      left: Math.min(nextCard.offsetLeft - track.offsetLeft, maxScroll),
      behavior: 'smooth',
    });
    setActiveCartSlide(nextIndex);
  };

  const startCartDrag = (event) => {
    const track = cartCarouselRef.current;
    if (!track) return;
    track.setPointerCapture?.(event.pointerId);
    dragState.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: track.scrollLeft,
    };
    track.classList.add('dragging');
  };

  const moveCartDrag = (event) => {
    const track = cartCarouselRef.current;
    if (!track || !dragState.current.active) return;
    event.preventDefault();
    track.scrollLeft = dragState.current.scrollLeft - (event.clientX - dragState.current.startX);
  };

  const stopCartDrag = (event) => {
    if (event?.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragState.current.active = false;
    cartCarouselRef.current?.classList.remove('dragging');
    window.setTimeout(updateCartSlide, 120);
  };

  return (
    <main className="detail-main">
      <section className="detail-layout">
        <aside>
          <span className="eyebrow">In This Section</span>
          <Link to="/closed-end-manual-tie-balers">Closed-End Manual Tie Balers</Link>
          <Link to="/open-end-automatic-tie-balers">Open-End Automatic Tie Balers</Link>
          <Link to="/wide-mouth-open-end-automatic-tie-balers">Wide-Mouth Open-End Automatic Tie Balers</Link>
          <Link to="/multi-materials-full-eject-closed-end-balers">Multi Materials Full-Eject Closed-End Balers</Link>
          <Link to="/open-end-manual-tie-balers">Open-End Manual Tie Balers</Link>
          <Link className="active" to="/cart-lifters-dumpers-dump-carts">Cart Lifters, Dumpers &amp; Dump Carts</Link>
          <Link to="/conveyors-bale-conveying-bagging-systems">Conveyors, Bale Conveying &amp; Bagging Systems</Link>
          <Link to="/used-clothing-balers">Used Clothing Balers</Link>
          <Link to="/cotton-gin-textile-baling-presses">Cotton Gin &amp; Textile Baling Presses</Link>
          <Link to="/biomass-cotton-gin-trash-baling-press">Biomass &amp; Cotton Gin Trash Baling Press</Link>
          <Link to="/vertical-down-packing-baling-presses-balers">Vertical &amp; Down-Packing Baling Presses &amp; Balers</Link>
          <Link to="/custom-designed-specialty-balers">Custom Designed &amp; Specialty Balers</Link>
          <Link to="/precrush-compactors-waste-compactors">PreCrush Compactors &amp; Waste Compactors</Link>
        </aside>
        <article className="article">
          <span className="eyebrow">Products</span>
          <h2>Cart Lifters, Dumpers &amp; Dump Carts</h2>
          <div className="body-copy page-summary-copy">Carts (sometimes called trucks) can significantly increase the efficiency of collecting and transporting production materials and waste. C+M cart lifters/dumpers speed-up cart unloading, reduce manual <strong>material handling</strong> and therefore help eliminate back strain. C+M offers multiple arrangements for cart lifters/dumpers. The C+M baler integrated cart lifter/dumper has the hydraulic power and operator controls incorporated into the C+M baler power unit and is directly attached to the baler. C+M cart/lifter dumpers can be used on C+M manual tie and automatic tie <strong>baling presses</strong>. </div>
          <div className="page-summary-notes">
              <span>Integrated or independently powered systems</span>
              <span>Safety guards and interlocks</span>
              <span>Steel and molded plastic dump cart options</span>
          </div>
          <PagePhotoViewer images={images} leadText={"For existing balers, compactors or other receptacles, C+M has independently powered & controlled cart lifters/dumpers that can be designed to fit your system. Whether integrated with a C+M baling system or an independent cart/lifter dumper, there are special applications when existing dump carts or gaylords can be utilized. C+M custom designed cart lifters/dumpers can be manufactured for handling two styles of dump carts when necessary, and with custom designed add-ons, the C+M cart/lifter dumper becomes an integral part of your material handling system. C+M also furnishes safety guards with baler integrated safety interlocks, remote operator controls and optional pull-up or swing-out safety gates. C+M offers different sizes of cart lifter/dumpers to fit all C+M balers and most other compactors and balers. Belt conveyors can also be loaded by C+M cart lifter/dumpers, each customizable to the specific application."} eyebrow="Equipment Photos" />
          <div className="content-stack">
            <section className="content-block">
              
              <div className="body-copy">Baler Series Heavy-Duty Integrated Cart Lifter/Gaylord Dumper C+M integrated cart lifter gaylord dumper CM30, CM34, CM50, CM60 &amp; CM78 Series Dumping Systems directly connected to the baler and utilizing the baler's power unit with 230V/460V TEFC 5-HP motor, double acting dual 4” diameter cylinders, hydraulic power and electric controls including safety guards for both sides of the lifter.</div>
            </section>
            <div className="gold-note">Note: Custom Designed as Needed for Predetermined Carts or Gaylords.</div>
            <section className="content-block">
              
              <div className="body-copy">Baling Systems offers a wide variety of standard and custom designed dump carts. The C+M steel carts are heavy duty and available in a wide variety of capacities with any required size fixed or rotating casters.

Baling Systems also offers high quality molded plastic Toter dump carts and custom designed plastic molded dump carts to fit virtually any material handling situation. The steel or molded plastic carts can be designed with 4 or 5 casters for heavier loads, both with forklift pockets.</div>
            </section>
            <section className="cart-carousel-section">
              <div className="cart-carousel-head">
                <div>
                  <span className="eyebrow">Dump Cart Options</span>
                  <h3>Available Dump Carts</h3>
                  <div className="body-copy">C+M offers heavy-load steel carts, molded plastic dump carts, mobile trucks, and tilt trucks for collecting and moving production materials, recyclables, and waste to the baling area.</div>
                </div>
              </div>
              <div
                className="cart-carousel"
                ref={cartCarouselRef}
                aria-label="Available dump cart options"
                onPointerDown={startCartDrag}
                onPointerMove={moveCartDrag}
                onPointerUp={stopCartDrag}
                onPointerCancel={stopCartDrag}
                onScroll={updateCartSlide}
                style={{ '--carousel-end-space': 'calc(100% - min(360px, 78vw))' }}
              >
                <figure className="cart-option-card">
                  <div className="cart-option-image">
                    <img src="/img/thumbnails/cart1.png" alt="Heavy-load dump cart CM463535BR 1.25 cubic yard" draggable="false" />
                  </div>
                  <figcaption>Heavy-load dump cart - CM463535BR 1.25 cubic yard</figcaption>
                </figure>
                <figure className="cart-option-card">
                  <div className="cart-option-image">
                    <img src="/img/thumbnails/cart2.png" alt="Heavy-load dump cart CM463535BR-5 Caster 1.25 cubic yard" draggable="false" />
                  </div>
                  <figcaption>Heavy-load dump cart - CM463535BR-5 Caster 1.25 cubic yard</figcaption>
                </figure>
                <figure className="cart-option-card">
                  <div className="cart-option-image">
                    <img src="/img/thumbnails/cart3.png" alt="Heavy-duty all steel dump cart 1.5 cubic yard" draggable="false" />
                  </div>
                  <figcaption>Heavy-duty all steel dump cart 1.5 cubic yard</figcaption>
                </figure>
                <figure className="cart-option-card">
                  <div className="cart-option-image">
                    <img src="/img/thumbnails/cart4.png" alt="Special low-cost poly-molded dump cart 1.68 cubic yard or 48.6 cubic feet" draggable="false" />
                  </div>
                  <figcaption>Special low-cost poly-molded dump cart 1.68 cubic yard or 48.6 cubic feet</figcaption>
                </figure>
                <figure className="cart-option-card">
                  <div className="cart-option-image">
                    <img src="/img/thumbnails/cart5.png" alt="Toter universal mobile truck 1 cubic yard with pintle hook towing assembly" draggable="false" />
                  </div>
                  <figcaption>Toter universal mobile truck - 1 cubic yard with pintle hook towing assembly</figcaption>
                </figure>
                <figure className="cart-option-card">
                  <div className="cart-option-image">
                    <img src="/img/thumbnails/cart6.png" alt="Special custom molded dump cart 0.9 cubic yard or 24.3 cubic feet" draggable="false" />
                  </div>
                  <figcaption>Special custom molded dump cart 0.9 cubic yard or 24.3 cubic feet</figcaption>
                </figure>
                <figure className="cart-option-card">
                  <div className="cart-option-image">
                    <img src="/img/thumbnails/cart7.png" alt="Special poly-molded tilt truck 2.2 cubic yard or 59 cubic feet" draggable="false" />
                  </div>
                  <figcaption>Special poly-molded tilt truck 2.2 cubic yard or 59 cubic feet</figcaption>
                </figure>
              </div>
              <div className="cart-carousel-controls" aria-label="Dump cart carousel controls">
                <button type="button" onClick={() => moveCartCarousel(-1)} aria-label="Show previous dump carts">
                  <ChevronLeft size={22} />
                </button>
                <div className="carousel-dots" aria-hidden="true">
                  {[0, 1, 2, 3, 4, 5, 6].map((index) => (
                    <span key={index} className={index === activeCartSlide ? 'active' : ''}></span>
                  ))}
                </div>
                <button type="button" onClick={() => moveCartCarousel(1)} aria-label="Show next dump carts">
                  <ChevronRight size={22} />
                </button>
              </div>
            </section>
      
          </div>
          <Link className="button" to="/contact/request-quote">Request a Quote <ArrowRight size={16} /></Link>
        </article>
      </section>
    </main>
  );
}

export default CartLiftersDumpersDumpCarts;
